import React from 'react'

import i18n from 'i18n-literally'
import { useConnect } from 'redux-bundler-hook'

import { Typography } from '@mui/material'

import BaseFormatter from '~/src/Annotations/Formatters/Base'
import {
  EMPTY_OBJECT,
  formattedDate,
  getDateTime,
  getTimeDiff,
} from '~/src/Lib/Utils'

const interruptionTitle = interruptionType => (props, interruption = interruptionType) => {
  const { annotation, entities } = props
  const { devices = EMPTY_OBJECT, models = EMPTY_OBJECT } = entities
  const { data, device: deviceId, subject } = annotation
  const { [deviceId]: device } = devices
  const { [device?.modelKey]: model } = models

  let serialNumber = data?.serialNumber ?? device?.serialNumber
  let deviceName = data?.deviceName
  if (device && device.puck) {
    const { [device.puck]: puck } = devices
    if (puck) {
      serialNumber = serialNumber ?? puck.serialNumber
      deviceName = deviceName ?? puck.deviceName
    }
  }
  const modelName = data?.modelName ?? model?.name

  if (serialNumber && (deviceName === serialNumber || !deviceName)) {
    return modelName
      ? i18n`${modelName} (${serialNumber}) ${interruption}`
      : i18n`${serialNumber} sensor ${interruption}`
  }

  if ([serialNumber, deviceName].some(Boolean)) {
    return serialNumber && deviceName
      ? i18n`${deviceName} (${serialNumber}) ${interruption}`
      : i18n`${deviceName} ${interruption}`
  }

  return subject
}

const keyToTitle = {
  DEVICE_UNPLUGGED: interruptionTitle('unplugged'),
  DEVICE_DISCONNECTED: interruptionTitle('disconnected'),
  GATEWAY_DISCONNECTED: props => {
    const { annotation, entities } = props
    const { devices = EMPTY_OBJECT } = entities
    const { data, device: deviceId, subject } = annotation
    const { [deviceId]: device } = devices

    if (!data && !device) return subject

    const deviceName = data?.deviceName ?? device?.deviceName
    const serialNumber = data?.serialNumber ?? device?.serialNumber

    const name = deviceName === serialNumber
      ? serialNumber
      : deviceName

    if (!name) return subject

    return `Gateway ${name} disconnected`
  },
  PUCK_DISCONNECTED: props => {
    const { annotation, entities } = props
    const { devices = EMPTY_OBJECT } = entities
    const { data, device: deviceId, subject } = annotation
    const { [deviceId]: device } = devices

    if (!device && !data) return subject

    const serial = data?.serialNumber ?? device?.serialNumber

    if (!serial) return subject

    return `${serial} disconnected`
  },
}

export const interruptionFormatterUtil = props => {
  const { annotation, entities } = props
  const { devices = EMPTY_OBJECT } = entities
  const { device: deviceId, key, subject, message } = annotation
  const { [deviceId]: device } = devices

  let title = subject
  if (key in keyToTitle) {
    const titleFormatter = keyToTitle[key]
    title = titleFormatter(props)
  }

  return {
    title,
    body: device ? '' : message
  }
}

export const InterruptionFormatterComponent = props => {
  const { annotation, color, entities, locale, ...passthruProps } = props
  const { devices = EMPTY_OBJECT } = entities
  const { device: deviceId, key, subject, message } = annotation
  const { [deviceId]: device } = devices

  let title = subject
  if (key in keyToTitle) {
    const titleFormatter = keyToTitle[key]
    title = titleFormatter(props)
  }

  return (
    <BaseFormatter
      wrap
      color={color}
      {...passthruProps}
      title={title}
      body={device ? (
        <Typography
          variant="body2"
          component="span"
          color="textSecondary"
          title={(
            device.lastCommunication
              ? formattedDate(device.lastCommunication, 'SHORT_DATETIME')
              : undefined
          )}
        >
          {device.online ? i18n`Now online. ` : i18n`Still offline. `}
          Reported:
          {i18n` ${
            device.lastCommunication
              ? getTimeDiff(getDateTime(device.lastCommunication))
              : 'never'
          }`}
        </Typography>
      ) : message}
    />
  )
}

export default React.memo(props => {
  const connectedProps = useConnect('selectEntities')

  return <InterruptionFormatterComponent {...connectedProps} {...props} />
})
